import React, { useEffect, useState, useCallback } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { BAILLEUR_LINKS } from './links';
import { bailleurApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { TID } from '@/constants/testIds';
import { Plus, MoreVertical, Trash2, Search, User, Home as HomeIcon, KeyRound, ClipboardCheck, ShieldCheck, AlertTriangle, ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Wizard } from '@/components/Wizard';
import { Loader } from '@/components/Loader';

function AddLocataireDialog({ onCreated, disabled = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const initialForm = {
    prenom: '', nom: '', telephone: '', email: '',
    identifier: '', password: '',
    adresse_logement: '', montant_loyer: '', devise: 'USD',
    jour_echeance: 5, duree_mois: 12,
  };

  const submit = async (form) => {
    setLoading(true);
    try {
      await bailleurApi.createLocataire({
        ...form,
        montant_loyer: parseFloat(form.montant_loyer),
        jour_echeance: parseInt(form.jour_echeance, 10),
      });
      toast.success('Locataire créé — pensez à lui transmettre ses identifiants.');
      setOpen(false);
      onCreated?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Impossible de créer le locataire.');
      throw err;
    } finally { setLoading(false); }
  };

  const steps = [
    {
      key: 'identity',
      title: t('wizard.locataire.step1'),
      icon: User,
      validate: (f) => {
        const e = [];
        if (!f.prenom?.trim()) e.push('Prénom requis');
        if (!f.nom?.trim()) e.push('Nom requis');
        if (!f.telephone?.trim()) e.push('Téléphone requis');
        return e;
      },
      render: (f, set) => (
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Prénom *</Label><Input data-testid={TID.lcPrenom} value={f.prenom} onChange={(e) => set('prenom', e.target.value)} className="mt-1.5" /></div>
          <div><Label>Nom *</Label><Input data-testid={TID.lcNom} value={f.nom} onChange={(e) => set('nom', e.target.value)} className="mt-1.5" /></div>
          <div><Label>Téléphone *</Label><Input data-testid={TID.lcTel} value={f.telephone} onChange={(e) => set('telephone', e.target.value)} className="mt-1.5" /></div>
          <div><Label>Email (optionnel)</Label><Input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} className="mt-1.5" /></div>
        </div>
      ),
    },
    {
      key: 'housing',
      title: t('wizard.locataire.step2'),
      icon: HomeIcon,
      validate: (f) => {
        const e = [];
        if (!f.adresse_logement?.trim()) e.push('Adresse du logement requise');
        const m = parseFloat(f.montant_loyer);
        if (!m || m <= 0) e.push('Montant du loyer invalide');
        return e;
      },
      render: (f, set) => (
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label>Adresse du logement *</Label>
            <Input data-testid={TID.lcAdresse} value={f.adresse_logement} onChange={(e) => set('adresse_logement', e.target.value)} className="mt-1.5" placeholder="Appt 4, Immeuble A, Kinshasa" /></div>
          <div><Label>Montant du loyer *</Label>
            <Input data-testid={TID.lcMontant} type="number" step="0.01" value={f.montant_loyer} onChange={(e) => set('montant_loyer', e.target.value)} className="mt-1.5" /></div>
          <div><Label>Devise</Label>
            <Select value={f.devise} onValueChange={(v) => set('devise', v)}>
              <SelectTrigger data-testid={TID.lcDevise} className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="CDF">CDF (Franc Congolais)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Jour d'échéance (1–28)</Label>
            <Input data-testid={TID.lcJour} type="number" min={1} max={28} value={f.jour_echeance} onChange={(e) => set('jour_echeance', e.target.value)} className="mt-1.5" /></div>
          <div><Label>Durée du bail (mois)</Label>
            <Input type="number" min={1} max={60} value={f.duree_mois} onChange={(e) => set('duree_mois', parseInt(e.target.value || '12'))} className="mt-1.5" /></div>
        </div>
      ),
    },
    {
      key: 'access',
      title: t('wizard.locataire.step3'),
      icon: KeyRound,
      validate: (f) => {
        const e = [];
        if (!f.identifier?.trim()) e.push('Identifiant requis');
        if (!f.password || f.password.length < 4) e.push('Mot de passe minimum 4 caractères');
        return e;
      },
      render: (f, set) => (
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Identifiant (email ou téléphone) *</Label>
              <Input data-testid={TID.lcIdent} value={f.identifier} onChange={(e) => set('identifier', e.target.value)} className="mt-1.5" placeholder="marie@example.com" /></div>
            <div><Label>Mot de passe *</Label>
              <Input data-testid={TID.lcPwd} value={f.password} onChange={(e) => set('password', e.target.value)} className="mt-1.5" placeholder="min. 4 caractères" /></div>
          </div>
          <div className="rounded-lg border border-[#BEDBFF] bg-[#E6F1FF]/40 p-3 text-xs text-slate-600">
            Vous êtes responsable de transmettre ces accès à votre locataire.
          </div>
        </div>
      ),
    },
    {
      key: 'review',
      title: t('wizard.locataire.step4'),
      icon: ClipboardCheck,
      render: (f) => (
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5 space-y-3 text-sm">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Récapitulatif</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><div className="text-xs text-slate-500">Nom complet</div><div className="font-semibold text-[#0D1B2A]">{f.prenom} {f.nom}</div></div>
            <div><div className="text-xs text-slate-500">Téléphone</div><div className="font-semibold text-[#0D1B2A]">{f.telephone}</div></div>
            <div className="sm:col-span-2"><div className="text-xs text-slate-500">Logement</div><div className="font-semibold text-[#0D1B2A]">{f.adresse_logement}</div></div>
            <div><div className="text-xs text-slate-500">Loyer</div><div className="font-semibold text-[#0D1B2A]">{f.montant_loyer} {f.devise} / mois</div></div>
            <div><div className="text-xs text-slate-500">Échéance</div><div className="font-semibold text-[#0D1B2A]">Le {f.jour_echeance} · {f.duree_mois} mois</div></div>
            <div className="sm:col-span-2"><div className="text-xs text-slate-500">Identifiant locataire</div><div className="font-semibold text-[#0D1B2A]">{f.identifier}</div></div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={disabled} title={disabled ? 'Capacité atteinte : augmentez votre offre' : undefined} data-testid={TID.addLocataire} className="bg-[#1677FF] hover:bg-[#0E62DB] disabled:cursor-not-allowed">
          <Plus className="w-4 h-4 mr-1.5" /> Ajouter un locataire
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#0D1B2A] flex items-center gap-2"><User className="w-5 h-5 text-[#1677FF]" /> {t('wizard.locataire.title')}</DialogTitle>
          <DialogDescription>Complétez les 4 étapes pour créer le compte du locataire.</DialogDescription>
        </DialogHeader>
        <Wizard
          steps={steps}
          initialForm={initialForm}
          onSubmit={submit}
          submitLabel={t('wizard.locataire.submit')}
          loading={loading}
          testidPrefix="lc-wizard"
        />
      </DialogContent>
    </Dialog>
  );
}

export default function BailleurLocataires() {
  const [items, setItems] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [locatairesResponse, subscriptionResponse] = await Promise.all([
        bailleurApi.listLocataires(),
        bailleurApi.subscription(),
      ]);
      setItems(locatairesResponse.data);
      setSubscription(subscriptionResponse.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (l) => {
    if (!window.confirm(`Supprimer ${l.prenom} ${l.nom} ?`)) return;
    await bailleurApi.deleteLocataire(l.id);
    toast.success('Locataire supprimé');
    load();
  };

  const filtered = items.filter((l) => !q || `${l.prenom} ${l.nom} ${l.telephone} ${l.adresse_logement}`.toLowerCase().includes(q.toLowerCase()));
  const currentLimit = Number(subscription?.subscription?.max_locataires || 0);
  const used = Number(subscription?.used || 0);
  const quotaReached = currentLimit > 0 && used >= currentLimit;

  return (
    <AppLayout links={BAILLEUR_LINKS} testid={TID.bailleurSidebar} title="Locataires">
      {quotaReached && (
        <div className="mb-5 flex flex-col gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div><div className="font-bold text-amber-950">Capacité de l’offre atteinte ({used}/{currentLimit})</div><div className="mt-0.5 text-sm text-amber-800">Vos locataires restent accessibles. Passez à l’offre supérieure pour en ajouter un nouveau.</div></div>
          </div>
          <Button onClick={() => nav('/bailleur/abonnement')} className="shrink-0 bg-amber-600 text-white hover:bg-amber-700">Voir les offres <ArrowUpRight className="ml-2 h-4 w-4" /></Button>
        </div>
      )}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="pl-9 h-10" />
        </div>
        <AddLocataireDialog onCreated={load} disabled={quotaReached} />
      </div>

      {loading ? (
        <Loader variant="full" />
      ) : filtered.length === 0 ? (
        <div className="lb-card p-10 text-center">
          <User className="w-8 h-8 mx-auto text-slate-400 mb-3" />
          <div className="text-lg font-semibold text-[#0D1B2A]">Aucun locataire pour le moment</div>
          <div className="text-sm text-slate-500 mt-1">Commencez par ajouter votre premier locataire.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((l) => (
            <div key={l.id} className="lb-card p-5 cursor-pointer" onClick={() => nav(`/bailleur/locataires/${l.id}`)}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-[#0D1B2A] text-base">{l.prenom} {l.nom}</div>
                  <div className="text-xs text-slate-500">{l.telephone}</div>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => nav(`/bailleur/locataires/${l.id}`)}>Voir le détail</DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => remove(l)} className="text-red-600 focus:text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <div className="mt-3 text-sm text-slate-600 line-clamp-1">{l.adresse_logement}</div>
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Loyer</div>
                  <div className="font-bold text-[#0D1B2A]">{l.montant_loyer} {l.devise}</div>
                </div>
                {l.echeances_impayees > 0 ? (
                  <Badge className="bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-50">{l.echeances_impayees} impayée(s)</Badge>
                ) : (
                  <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50">À jour</Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
